import { authenticatedAuthHttpClient, authHttpClient } from './client'
import { AccessTokenCreatePayload } from '../models/AccessToken'
import { AxiosResponse } from 'axios'
import { ConsoleUser } from '../models/ConsoleUser'
import { MFAChallengeResponse } from '../models/MFA'

export type Dashboard = {
  id: string
  name: string
}

type LoginResponse = {
  access_token: string
  refresh_token: string
  challengeName: string
  session: any
}

export type BiDashboardsResponse = {
  biToken: string
  dashboards: Dashboard[]
}

type RefreshTokenResponse = {
  access_token: string
  refresh_token: string
}

export type DashboardFilter = {
  filterKey: string
  filterValue: string
  filterOperator: string
}

export const loginWithUsernamePasswordByAccountId = (accountId: string, username: string, password: string): Promise<AxiosResponse<LoginResponse>> => {
  return authHttpClient.postForm(`/accounts/${accountId}/console/login`, {
    username,
    password,
  })
}

export const loginWithUsernamePasswordConsoleV2 = (email: string, password: string): Promise<AxiosResponse<LoginResponse>> => {
  return authHttpClient.postForm(`/v2/console/login`, {
    email,
    password,
  })
}

export const loginWithMFATokenConsoleV2 = (token: string, challengeResponse: MFAChallengeResponse): Promise<AxiosResponse<LoginResponse>> => {
  return authHttpClient.post(`/v2/console/mfa-login`, challengeResponse, { headers: { Authorization: `Bearer ${token}` } })
}

export const refreshTokenRequest = (accountId: string, refreshToken: string): Promise<AxiosResponse<RefreshTokenResponse>> => {
  return authHttpClient.post(`/v1/accounts/${accountId}/token/refresh`, null, { headers: { Authorization: `Bearer ${refreshToken}` } })
}

export const crossAccountLoginRequest = (targetAccountId: string, token: string): Promise<AxiosResponse<RefreshTokenResponse>> => {
  return authHttpClient.post(`/v2/console/cross-account-login`, { targetAccountId }, { headers: { Authorization: `Bearer ${token}` } })
}

export const getConsoleTokenForAssociationCode = (code: string): Promise<AxiosResponse<LoginResponse>> => {
  return authHttpClient.postForm(`/v2/console/oauth2/token`, {
    code,
  })
}

export const signUpWithUsernamePasswordConsoleV2 = (email: string, password: string, state: string | null): Promise<AxiosResponse> => {
  return authHttpClient.postForm('/v2/console/signup', {
    email,
    password,
    ...(state && { state: state }),
  })
}

export const confirmSignUpWithVerificationCodeConsole = (email: string, code: string): Promise<AxiosResponse<LoginResponse>> => {
  return authHttpClient.postForm('/v2/console/confirm-signup', {
    email,
    code,
  })
}

export const forgotPassword = (email: string): Promise<AxiosResponse> => {
  return authHttpClient.postForm('/v2/console/forgot-password', {
    email,
  })
}

export const confirmForgotPassword = (email: string, password: string, code: string): Promise<AxiosResponse> => {
  return authHttpClient.postForm('/v2/console/confirm-forgot-password', {
    email,
    password,
    code,
  })
}

export const confirmRegistrationByAccountId = (accountId: string, username: string, newPassword: string, session: string) => {
  return authHttpClient.postForm(`/accounts/${accountId}/console/confirm-registration`, {
    username,
    newPassword,
    session,
  })
}

export const createExternalAccessTokenByAccountId = (accountId: string, payload: AccessTokenCreatePayload) => {
  return authenticatedAuthHttpClient.post(`/v1/accounts/${accountId}/token`, payload)
}

export const getBiDashboardsByAccountId = (
  accountId: string,
  filters?: Array<DashboardFilter> | null,
  dashboardIds?: Array<string> | null,
): Promise<AxiosResponse<BiDashboardsResponse>> => {
  const params = {
    filters: filters,
    internalIds: dashboardIds,
  }

  return authenticatedAuthHttpClient.post(`/v1/accounts/${accountId}/bi-dashboards`, { ...params })
}

export const inviteConsoleUserByAccountId = (accountId: string, payload: ConsoleUser) => {
  return authenticatedAuthHttpClient.post(`/v2/accounts/${accountId}/invite`, payload)
}
